import { getApp, getApps, initializeApp } from "firebase/app";
import {
  Auth,
  OAuthProvider,
  UserCredential,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  initializeAuth,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

// Firebase's client identifiers are public application configuration. Keep
// Observer's dedicated defaults in source so production/mobile builds cannot
// silently fall back to an unconfigured auth screen when an ignored .env file
// is absent. Environment values remain available for isolated test projects.
const config = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    "AIzaSyAFgpPGHjW2vvY7X0Wh4cYrkyz1nk07OoY",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    "observer-theory-prod.firebaseapp.com",
  projectId:
    process.env.REACT_APP_FIREBASE_PROJECT_ID || "observer-theory-prod",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    "observer-theory-prod.firebasestorage.app",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1079895378921",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    "1:1079895378921:web:231f3094ce79001df6d978",
};

export const firebaseReady = Boolean(
  config.apiKey && config.authDomain && config.projectId && config.appId,
);

let authInstance: Auth | undefined;
export const observerAuth = (): Auth => {
  if (!firebaseReady)
    throw new Error(
      "Account services are being provisioned. Please try again soon.",
    );
  if (authInstance) return authInstance;
  const app = getApps().length ? getApp() : initializeApp(config);
  authInstance = Capacitor.isNativePlatform()
    ? initializeAuth(app, {
        persistence: [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          inMemoryPersistence,
        ],
      })
    : getAuth(app);
  return authInstance;
};

export const waitForObserverAuth = () =>
  new Promise<ReturnType<typeof observerAuth>["currentUser"]>((resolve) => {
    const auth = observerAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

const retainCredential = async (credential: UserCredential) => {
  localStorage.setItem("apiToken", await credential.user.getIdToken());
  localStorage.setItem("observerUserEmail", credential.user.email || "");
  return credential;
};

export const signInWithProvider = async (provider: "google" | "apple") => {
  const auth = observerAuth();
  const selected =
    provider === "google"
      ? new GoogleAuthProvider()
      : new OAuthProvider("apple.com");
  selected.setCustomParameters({ prompt: "select_account" });
  if (!Capacitor.isNativePlatform())
    return retainCredential(await signInWithPopup(auth, selected));
  if (provider === "google") {
    const result = await FirebaseAuthentication.signInWithGoogle();
    const credential = GoogleAuthProvider.credential(
      result.credential?.idToken,
      result.credential?.accessToken,
    );
    return retainCredential(await signInWithCredential(auth, credential));
  }
  const result = await FirebaseAuthentication.signInWithApple();
  if (!result.credential?.idToken)
    throw new Error("Apple sign-in did not return a credential.");
  const credential = (selected as OAuthProvider).credential({
    idToken: result.credential.idToken,
    rawNonce: result.credential.nonce,
  });
  return retainCredential(await signInWithCredential(auth, credential));
};

export const emailAccount = async (
  email: string,
  password: string,
  register: boolean,
) => {
  const auth = observerAuth();
  const credential = register
    ? await createUserWithEmailAndPassword(auth, email, password)
    : await signInWithEmailAndPassword(auth, email, password);
  return retainCredential(credential);
};

export const resetAccountPassword = (email: string) =>
  sendPasswordResetEmail(observerAuth(), email);

export const signOutObserver = async () => {
  if (Capacitor.isNativePlatform()) {
    await FirebaseAuthentication.signOut();
  }
  await observerAuth().signOut();
  localStorage.removeItem("apiToken");
  localStorage.removeItem("observerUserEmail");
};
